/**
 * CallCard entity model — ES module version of the data shape from CallCards.ts
 *
 * Contains the CallCard interface + factory (no side effects).
 * Controller logic is in call-card-controller.ts, realtime in call-card-realtime.ts.
 */

import type {
  CallCardData,
  CallCardClientGroupData,
  CallCardClientGroupSiteData,
  OfficerData,
  CCPersonData,
  CCVehicleData,
  AttachmentData,
  AttachmentInfoData,
  CallCommentData,
  CallCardStatus as CallCardStatusEnum,
  CCAction as CCActionEnum,
  CallCardInProgressStatus as CallCardInProgressStatusEnum,
  MDTInProgressStatus as MDTInProgressStatusEnum,
} from '@/services/api-types';

export type {
  CallCardData,
  CallCardClientGroupData,
  CallCardClientGroupSiteData,
  CallCommentData,
};

// Re-export enums from api-types
export {
  CallCardStatus,
  CCAction,
  CallCardInProgressStatus,
  MDTInProgressStatus,
} from '@/services/api-types';

// ── Date field helpers ───────────────────────────────────────────────

export const CALL_CARD_DATE_FIELDS = [
  'createdAt',
  'callDateTime',
  'primaryDispatched',
  'primaryEnroute',
  'primaryOnScene',
  'primaryCleared',
  'whenPrimaryDispatchedChanged',
  'whenPrimaryEnrouteChanged',
  'whenPrimaryOnSceneChanged',
  'whenPrimaryClearedChanged',
  'dispatched',
  'enroute',
  'onScene',
  'cleared',
  'whenDispatchedChanged',
  'whenEnrouteChanged',
  'whenOnSceneChanged',
  'whenClearedChanged',
] as const;

export function isCallCardDateField(name: string): boolean {
  return (CALL_CARD_DATE_FIELDS as readonly string[]).includes(name);
}

export const PRIMARY_OFFICER_FIELDS = [
  'primaryOfficer',
  'primaryOfficerName',
  'primaryDispatched',
  'primaryEnroute',
  'primaryOnScene',
  'primaryCleared',
] as const;

export function isPrimaryOfficerField(name: string): boolean {
  return (PRIMARY_OFFICER_FIELDS as readonly string[]).includes(name);
}

// ── Factory ──────────────────────────────────────────────────────────

export function createCallCard(data?: Partial<CallCardData>): CallCardData {
  return {
    id: 0,
    prevCallCardId: 0,
    createdAt: null,
    eventId: '',
    relatedCardsId: 0,
    company: 0,
    callDateTime: null,
    callLocName: '',
    callLocAddress: '',
    callLocAddress2: '',
    callLocCity: '',
    callLocState: '',
    callLocZip: '',
    callLocCountry: '',
    callLocFormattedAddress: '',
    latitude: null,
    longitude: null,
    learnedAddressId: null,
    customLocationId: null,
    callerName: '',
    callerPhone: '',
    callerLocation: '',
    callDisposition: 0,
    callDispositionValue: '',
    dispositionNotes: '',
    primaryOfficer: 0,
    primaryOfficerName: '',
    primaryDispatched: null,
    whenPrimaryDispatchedChanged: null,
    primaryEnroute: null,
    whenPrimaryEnrouteChanged: null,
    primaryOnScene: null,
    whenPrimaryOnSceneChanged: null,
    primaryCleared: null,
    whenPrimaryClearedChanged: null,
    ccStatus: 0,
    author: 0,
    priority: 5,
    primaryStartingMileage: 0,
    primaryEndingMileage: 0,
    natureOfCall: [],
    natureOfCallValues: [],
    backupUnits: [],
    queuedUnits: [],
    people: [],
    vehicles: [],
    attachmentList: [],
    attachmentInfoList: [],
    callCommentsToAttach: [],
    client: { id: 0, name: '', sites: [], allSites: [] },
    citationCount: 0,
    updateAndReclose: false,
    ...data,
  };
}
